import type { ControllerProps } from 'react-hook-form'
import type { TextFieldProps } from '@mui/material/TextField'
import type { GridItemProps } from 'common-ui/components/GridItem'
import CommonTextInput from 'common-ui/components/TextInput'
import { validate } from '../../../utils'

type TextInputProps<T> = TextFieldProps &
  GridItemProps & {
    name: ControllerProps['name']
    multiline?: boolean
    noNegative?: boolean
    unsignedInt?: boolean
    validators?: Parameters<typeof validate>[0]
    showPassword?: { show: boolean; id: string }
    variant?: TextFieldProps['variant']
    transform?: { displayAs: (value: T) => string | number; storeAs: (value: T | '') => T | null }
  }

/**
 * Network-manager TextInput wrapper that adds custom `validators` support
 * on top of the common-ui TextInput component.
 */
const TextInput = <T = string,>({ validators = {}, ...props }: TextInputProps<T>) => {
  const applyDisplayAs = (value: unknown): unknown =>
    value != null && value !== '' ? (props.transform?.displayAs(value as T) ?? value) : ''

  if (props.noNegative) {
    validators.number = {
      greaterThanOrEqualTo: 0,
      message: 'Cannot be negative',
      ...validators.number,
    }
  }

  return (
    <CommonTextInput
      {...props}
      data-test-id={`textinput-${props.name}`}
      rules={{
        validate: (value) =>
          validate({
            ...validators,
            presence: props.required,
          })(applyDisplayAs(value)),
      }}
    />
  )
}

export default TextInput
